import { TFunction } from 'i18next';
import { KpiObjectType } from '@/api/entities/kpi';
import { getKpiObjectLabels } from '@/utils/kpi-dashboard.utils';
import Card from '@/components/Card';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { IconAlertTriangle, IconTrophy } from '@tabler/icons-react';
import { KpiModel, KpiModelSummary } from '@/type/kpi-dashboard.types';
import { ClassRankingCard } from './ClassRankingCard';
import { EvaluatedImagesCard } from './EvaluatedImagesCard';
import { IouDistributionChart } from './IouDistributionChart';
import { MIouGauge } from './MIouGauge';
import { KpiChartCard } from './KpiChartCard';
import { TierBreakdownCardList } from './TierBreakdownCardList';
import { InfoTip } from '@/components/Help/InfoTip';
import { DASHBOARD_TIPS, getKpiTip } from '@/docs/tips/dashboard';

interface KpiOverviewPanelProps {
  t?: TFunction<undefined>;
  type: KpiObjectType;
  model?: KpiModel;
  summary?: KpiModelSummary;
}

export const KpiOverviewPanel = ({
  t,
  type,
  model,
  summary,
}: KpiOverviewPanelProps) => {
  const { objects } = getKpiObjectLabels(t, type);

  return (
    <Card
      title={
        <TitleSection text={t('overview')} defaultIconClassName="ki-graph-2" />
      }
      bodyClassName="py-8 flex flex-col gap-5"
    >
      {!(model && summary) ? (
        <NotFound text={`${t('common:noDataFound')}...`} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <EvaluatedImagesCard
              t={t}
              type={type}
              evaluatedImages={model?.evaluatedImages ?? 0}
              classCount={summary?.classCount ?? 0}
            />
            <div className="relative rounded-xl border border-gray-DBDFE9 bg-white p-5">
              <InfoTip
                className="absolute right-4 top-4"
                tip={getKpiTip(DASHBOARD_TIPS.kpiMIou, type)}
              />
              {/* Capped so the gauge keeps its proportions on a full-width panel. */}
              <MIouGauge
                value={summary?.mIou ?? 0}
                containerClassName="mx-auto max-w-xs"
              />
            </div>
          </div>

          <TierBreakdownCardList
            t={t}
            type={type}
            tierCounts={summary?.tierCounts}
          />

          <KpiChartCard
            title={t('iouDistribution')}
            description={t('numberOfObjectsPerIouBucket', { objects })}
            infoTip={getKpiTip(DASHBOARD_TIPS.kpiIouDistribution, type)}
          >
            <IouDistributionChart
              t={t}
              type={type}
              buckets={summary?.distribution ?? []}
            />
          </KpiChartCard>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ClassRankingCard
              t={t}
              title={t('topFiveObjects', { objects })}
              tier="strong"
              icon={<IconTrophy className="-mt-0.75 h-5.5 w-5.5" stroke={2} />}
              classes={summary?.topClasses ?? []}
              infoTip={getKpiTip(DASHBOARD_TIPS.kpiTopFive, type)}
            />
            <ClassRankingCard
              t={t}
              title={t('flopFiveObjects', { objects })}
              tier="weak"
              icon={
                <IconAlertTriangle
                  className="-mt-[1.5px] h-[20.61px] w-[20.61px]"
                  stroke={2}
                />
              }
              classes={summary?.flopClasses ?? []}
              infoTip={getKpiTip(DASHBOARD_TIPS.kpiFlopFive, type)}
            />
          </div>
        </div>
      )}
    </Card>
  );
};
